import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, CheckCircle, Clock, AlertCircle, ArrowRight, Package, History, Activity, Clipboard, FileText, FileSpreadsheet, ChevronLeft, ChevronRight, Droplets, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, addDays, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import ExportButtons from './ExportButtons';
import RoomAssignmentModal from './RoomAssignmentModal';
import TaskPhotoUpload from './TaskPhotoUpload';
import TaskPhotoThumb from './TaskPhotoThumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompostManagement = ({ showBatchReminders = false, showReadyForSpawning = false }) => {
  const [batches, setBatches] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchDetail, setShowBatchDetail] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRoomAssignmentModal, setShowRoomAssignmentModal] = useState(null);
  const [showDateAdjustModal, setShowDateAdjustModal] = useState(null);
  const [showEditNameModal, setShowEditNameModal] = useState(null);
  const [editBatchName, setEditBatchName] = useState('');
  const [readyBatches, setReadyBatches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomCycles, setRoomCycles] = useState([]);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [batchesRes, scheduleRes, tasksRes, roomsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/compost/batches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/compost/schedule`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/compost/today-tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setBatches(batchesRes.data);
      setSchedule(scheduleRes.data.schedule);
      // Trust backend filtering — show everything it returns (action items
      // first, then in-progress/monitoring items).
      setTodayTasks(tasksRes.data.tasks || []);
      setRooms(roomsRes.data);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);

      // Find batches ready for spawning (Day 20 completed)
      const today = new Date();
      const ready = batchesRes.data.filter(batch => {
        if (batch.status !== 'active') return false;
        const start = new Date(batch.start_date);
        const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
        return daysPassed >= 20;
      });
      setReadyBatches(ready);

      // Calculate upcoming batch starts - for composting supervisor
      if (showBatchReminders) {
        const activeBatches = batchesRes.data.filter(b => b.status === 'active');
        // Sort by start_date descending to get latest batch first
        const sortedBatches = [...activeBatches].sort((a, b) => 
          new Date(b.start_date) - new Date(a.start_date)
        );
        const lastBatch = sortedBatches[0];
        if (lastBatch) {
          const lastStart = new Date(lastBatch.start_date);
          const upcoming = [];
          // Next 4 prewetting dates every 3 days after the last batch
          for (let i = 1; i <= 4; i++) {
            const nextStart = addDays(lastStart, i * 3);
            if (nextStart > today && nextStart <= addDays(today, 30)) {
              upcoming.push({
                date: nextStart,
                batchNumber: batchesRes.data.length + i,
                type: 'prewetting'
              });
            }
          }
          setUpcomingBatches(upcoming);
        }
        
        // Also calculate upcoming spawning dates from active batches
        const upcomingSpawning = activeBatches.map(batch => {
          const start = new Date(batch.start_date);
          const spawningDate = addDays(start, 19); // Day 20
          const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
          if (daysPassed < 20 && spawningDate <= addDays(today, 14)) {
            return {
              batchName: batch.batch_name,
              spawningDate: spawningDate,
              daysUntil: Math.floor((spawningDate - today) / (1000 * 60 * 60 * 24))
            };
          }
          return null;
        }).filter(Boolean);
        
        // Store in state - we'll use upcomingBatches for both
        if (upcomingSpawning.length > 0) {
          setUpcomingBatches(prev => [...prev, ...upcomingSpawning.map(s => ({
            date: s.spawningDate,
            batchName: s.batchName,
            daysUntil: s.daysUntil,
            type: 'spawning'
          }))]);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch composting data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const activeBatches = batches.filter(b => b.status === 'active');
  const pastBatches = batches.filter(b => b.status === 'completed' || b.status === 'moved_to_room');

  // Get room name for a batch
  const getRoomForBatch = (batchId) => {
    const cycle = roomCycles.find(c => c.batch_id === batchId && c.status === 'active')
      || roomCycles.find(c => c.batch_id === batchId);
    if (cycle) {
      const room = rooms.find(r => r.id === cycle.room_id);
      return room ? room.name : null;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Upcoming Dates Reminders - Only for Composting Supervisor/Owner */}
      {showBatchReminders && upcomingBatches.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="batch-reminders-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Calendar className="w-5 h-5" />
              Upcoming Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Prewetting (New Batch Start) Reminders */}
              {upcomingBatches.filter(b => b.type === 'prewetting').length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-blue-800 uppercase mb-2">New Batch Start (Prewetting)</p>
                  {upcomingBatches.filter(b => b.type === 'prewetting').map((batch, index) => (
                    <div key={`prewet-${index}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200 mb-2">
                      <div>
                        <p className="font-medium text-gray-900">Batch #{batch.batchNumber}</p>
                        <p className="text-sm text-gray-600">Start prewetting on {format(batch.date, 'PPP')}</p>
                      </div>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {Math.ceil((batch.date - new Date()) / (1000 * 60 * 60 * 24))} days
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Spawning Reminders */}
              {upcomingBatches.filter(b => b.type === 'spawning').length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-800 uppercase mb-2">Upcoming Spawning</p>
                  {upcomingBatches.filter(b => b.type === 'spawning').map((batch, index) => (
                    <div key={`spawn-${index}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200 mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{batch.batchName}</p>
                        <p className="text-sm text-gray-600">Ready for spawning on {format(batch.date, 'PPP')}</p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        {batch.daysUntil} days
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batches Ready for Room Assignment - Only for Composting Supervisor */}
      {showReadyForSpawning && readyBatches.length > 0 && (
        <Card className="border-green-200 bg-green-50/50" data-testid="ready-for-room-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-green-900">
                <Package className="w-5 h-5" />
                Batches Ready for Room Assignment ({readyBatches.length})
              </CardTitle>
              <Button
                data-testid="assign-batch-to-room-button"
                onClick={() => setShowAssignModal(true)}
                className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Assign to Room
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {readyBatches.map((batch) => {
                const start = new Date(batch.start_date);
                const today = new Date();
                const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
                const assignedCycle = roomCycles.find(c => c.batch_id === batch.id && c.status === 'active');
                const assignedRoom = assignedCycle ? rooms.find(r => r.id === assignedCycle.room_id) : null;
                
                return (
                  <div key={batch.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium text-gray-900">{batch.batch_name}</p>
                      <p className="text-sm text-gray-600">Day {daysPassed} - Composting complete, ready for spawning</p>
                    </div>
                    {assignedRoom ? (
                      <Badge className="bg-blue-100 text-blue-800">Assigned to {assignedRoom.name}</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800">Not Assigned</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Composting Management</CardTitle>
            <Button
              data-testid="create-batch-button"
              onClick={() => setShowCreateModal(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Batch
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="today" className="w-full">
            <TabsList className="mb-4 bg-gray-100 flex-wrap">
              <TabsTrigger value="today" className="flex items-center gap-2" data-testid="todays-work-tab">
                <Clipboard className="w-4 h-4" />
                Today's Work ({todayTasks.length})
              </TabsTrigger>
              <TabsTrigger value="active" className="flex items-center gap-2" data-testid="active-batches-tab">
                <Activity className="w-4 h-4" />
                Active ({activeBatches.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="flex items-center gap-2" data-testid="past-batches-tab">
                <History className="w-4 h-4" />
                Past ({pastBatches.length})
              </TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-2" data-testid="calendar-tab">
                <Calendar className="w-4 h-4" />
                Calendar
              </TabsTrigger>
            </TabsList>

            {/* Today's Work Tab */}
            <TabsContent value="today">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading tasks...</div>
              ) : todayTasks.length === 0 ? (
                <div className="py-12 text-center text-gray-500 border border-dashed rounded-lg">
                  No composting tasks requiring action today
                </div>
              ) : (
                <div className="space-y-3">
                  {todayTasks.map((task) => {
                    const isMonitoring = !task.action_required;
                    const cardBg = task.completed
                      ? 'bg-white border-gray-200'
                      : isMonitoring
                        ? 'bg-gray-50 border-gray-300'
                        : 'bg-yellow-50 border-yellow-300';
                    const iconColor = task.completed
                      ? 'text-green-600'
                      : isMonitoring
                        ? 'text-gray-500'
                        : 'text-yellow-600';
                    return (
                      <div
                        key={`${task.batch_id}-${task.day}`}
                        className={`flex items-center justify-between p-4 rounded-lg border ${cardBg}`}
                        data-testid={`task-${task.batch_id}-${task.day}`}
                      >
                        <div className="flex items-center gap-3">
                          {task.completed ? (
                            <CheckCircle className={`w-5 h-5 ${iconColor}`} />
                          ) : (
                            <AlertCircle className={`w-5 h-5 ${iconColor}`} />
                          )}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900">{task.batch_name} - Day {task.day}</p>
                              {isMonitoring && (
                                <Badge variant="outline" className="text-[10px] text-gray-600 border-gray-300">
                                  In progress
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{task.task}</p>
                            <p className="text-xs text-gray-500 mt-1">{task.description}</p>
                          </div>
                        </div>
                        {!task.completed && task.action_required && (
                          <CompleteTaskButton
                            task={task}
                            onComplete={fetchData}
                            onSpawningComplete={(batch) => setShowRoomAssignmentModal(batch)}
                          />
                        )}
                        {task.completed && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                            Completed
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Active Batches Tab */}
            <TabsContent value="active">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading batches...</div>
              ) : activeBatches.length === 0 ? (
                <div className="py-12 text-center text-gray-500 border border-dashed rounded-lg">
                  No active composting batches. Create one to start tracking.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="active-batches-grid">
                  {activeBatches.map((batch) => (
                    <BatchCard 
                      key={batch.id} 
                      batch={batch} 
                      schedule={schedule} 
                      onClick={() => setShowBatchDetail(batch)}
                      onAdjustDates={() => setShowDateAdjustModal(batch)}
                      onEditName={(b) => {
                        setShowEditNameModal(b);
                        setEditBatchName(b.batch_name);
                      }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Past Batches Tab */}
            <TabsContent value="past">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading batches...</div>
              ) : pastBatches.length === 0 ? (
                <div className="py-12 text-center text-gray-500 border border-dashed rounded-lg">
                  No completed batches yet.
                </div>
              ) : (
                <>
                  <div className="flex justify-end mb-4">
                    <ExportButtons type="batch-history" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="past-batches-grid">
                    {pastBatches.map((batch) => (
                      <BatchCard 
                        key={batch.id} 
                        batch={batch} 
                        schedule={schedule} 
                        onClick={() => setShowBatchDetail(batch)} 
                        isPast 
                        assignedRoom={getRoomForBatch(batch.id)}
                        onAssignRoom={(b) => setShowRoomAssignmentModal(b)}
                      />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Calendar Tab - Composting Schedule with Export */}
            <TabsContent value="calendar">
              <CompostingCalendarFull batches={batches} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modals */}
      {showCreateModal && (
        <CreateBatchModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchData();
            toast.success('Batch created successfully');
          }}
        />
      )}

      {showBatchDetail && (
        <BatchDetailModal
          batch={showBatchDetail}
          schedule={schedule}
          onClose={() => setShowBatchDetail(null)}
          onUpdate={fetchData}
        />
      )}

      {showAssignModal && (
        <BatchToRoomAssignmentModal
          batches={readyBatches.filter(b => !roomCycles.find(c => c.batch_id === b.id && c.status === 'active'))}
          rooms={rooms.filter(r => r.status === 'idle' || !roomCycles.find(c => c.room_id === r.id && c.status === 'active'))}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            setShowAssignModal(false);
            fetchData();
            toast.success('Batch assigned to room successfully');
          }}
        />
      )}

      {/* Room Assignment Modal - shown after Spawning completion */}
      {showRoomAssignmentModal && (
        <RoomAssignmentModal
          isOpen={true}
          batch={showRoomAssignmentModal}
          onClose={() => setShowRoomAssignmentModal(null)}
          onAssignmentComplete={() => {
            setShowRoomAssignmentModal(null);
            fetchData();
          }}
        />
      )}

      {/* Compost Batch Date Adjustment Modal */}
      {showDateAdjustModal && (
        <CompostDateAdjustModal
          batch={showDateAdjustModal}
          onClose={() => setShowDateAdjustModal(null)}
          onSuccess={() => {
            setShowDateAdjustModal(null);
            fetchData();
          }}
        />
      )}

      {/* Edit Batch Name Modal */}
      {showEditNameModal && (
        <Dialog open={true} onOpenChange={() => setShowEditNameModal(null)}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Edit Batch Name</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-batch-name">Batch Name</Label>
                <Input
                  id="edit-batch-name"
                  data-testid="edit-batch-name-input"
                  value={editBatchName}
                  onChange={(e) => setEditBatchName(e.target.value)}
                  placeholder="Enter new batch name"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditNameModal(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                  onClick={async () => {
                    if (!editBatchName.trim()) {
                      toast.error('Please enter a batch name');
                      return;
                    }
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(
                        `${API}/compost/batches/${showEditNameModal.id}/update-name`,
                        { batch_name: editBatchName.trim() },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success('Batch name updated');
                      setShowEditNameModal(null);
                      fetchData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update batch name');
                    }
                  }}
                  data-testid="save-batch-name-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const CompleteTaskButton = ({ task, onComplete, onSpawningComplete }) => {
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [photoId, setPhotoId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isSpawningTask = task.day === 20 && task.task.toLowerCase().includes('spawning');
  const photoRecommended = /turning|tunnel|spawning/i.test(task.task || '');

  const handleComplete = async () => {
    if (photoRecommended && !photoId) {
      const ok = window.confirm(`A photo is recommended for "${task.task}". Complete without a photo?`);
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/compost/complete-task`, {
        batch_id: task.batch_id,
        day: task.day,
        notes: notes || null,
        photo_id: photoId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Task completed!');
      setShowModal(false);
      setPhotoId(null);
      setNotes('');
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        data-testid="complete-task-button"
        onClick={(e) => {
          e.stopPropagation();
          // Day-20 spawning MUST assign a room — open the room assignment modal
          // directly instead of completing first (prevents "spawned without room").
          if (isSpawningTask && onSpawningComplete) {
            onSpawningComplete({ id: task.batch_id, batch_name: task.batch_name });
          } else {
            setShowModal(true);
          }
        }}
        size="sm"
        className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
      >
        {isSpawningTask ? 'Complete & Assign Room' : 'Complete Task'}
      </Button>

      {showModal && (
        <Dialog open={true} onOpenChange={setShowModal}>
          <DialogContent onClick={(e) => e.stopPropagation()} data-testid="complete-task-modal">
            <DialogHeader>
              <DialogTitle>Complete Task - {task.task}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">{task.batch_name}</p>
                <p className="text-xs text-gray-500 mt-1">Day {task.day}: {task.description}</p>
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="task-notes-input"
                  placeholder="Any observations or notes about this task..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div>
                <Label className="mb-1 block">Photo of the compost (recommended)</Label>
                <p className="text-xs text-gray-500 mb-2">Snap a picture so the Grower & Owner can see the finished compost.</p>
                <TaskPhotoUpload onUploaded={setPhotoId} testIdPrefix="compost-complete" />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-complete-button"
                  onClick={handleComplete}
                  disabled={submitting}
                  className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                >
                  {submitting ? 'Completing...' : 'Mark Complete'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// BatchCard Component for displaying batch information
const BatchCard = ({ batch, schedule, onClick, isPast = false, assignedRoom = null, onAdjustDates = null, onEditName = null, onAssignRoom = null }) => {
  const getDayProgress = (batch) => {
    const start = new Date(batch.start_date);
    const today = new Date();
    const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(daysPassed, 20);
  };

  const currentDay = getDayProgress(batch);
  const progress = (currentDay / 20) * 100;
  const currentTask = schedule.find(s => s.day === currentDay);

  return (
    <Card
      className={`border-gray-200 cursor-pointer hover:shadow-lg transition-all ${isPast ? 'opacity-80' : ''}`}
      onClick={onClick}
      data-testid={`batch-card-${batch.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-medium">{batch.batch_name}</CardTitle>
              {!isPast && batch.status === 'active' && onEditName && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditName(batch);
                  }}
                  data-testid={`edit-name-btn-${batch.id}`}
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {isPast ? 'Completed' : 'Started'}: {format(new Date(batch.start_date), 'PP')}
            </p>
          </div>
          <Badge className={batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            {batch.status === 'moved_to_room' ? 'In Room' : batch.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isPast && (
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Day {currentDay} of 20</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#1A4D2E] h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
        
        {currentTask && batch.status === 'active' && !isPast && (
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current Task</p>
            <p className="text-sm font-medium text-gray-900">{currentTask.task}</p>
          </div>
        )}

        {isPast && batch.spawning_date && (
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Spawning Date</p>
            <p className="text-sm font-medium text-gray-900">{format(new Date(batch.spawning_date), 'PP')}</p>
          </div>
        )}

        {/* Show assigned room for past batches */}
        {isPast && assignedRoom && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Assigned To</p>
            <p className="text-sm font-bold text-blue-800">{assignedRoom}</p>
          </div>
        )}

        {/* Assign / Change room for completed (spawned) batches */}
        {isPast && onAssignRoom && (
          <Button
            size="sm"
            variant="outline"
            className={`w-full mt-2 ${assignedRoom ? 'text-blue-700 border-blue-600 hover:bg-blue-50' : 'text-[#1A4D2E] border-[#1A4D2E] hover:bg-[#1A4D2E]/10'}`}
            onClick={(e) => {
              e.stopPropagation();
              onAssignRoom(batch);
            }}
            data-testid={`assign-room-btn-${batch.id}`}
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            {assignedRoom ? 'Change Room' : 'Assign Room'}
          </Button>
        )}

        {/* Adjust Dates button for active batches */}
        {!isPast && batch.status === 'active' && onAdjustDates && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-2 text-[#1A4D2E] border-[#1A4D2E] hover:bg-[#1A4D2E]/10"
            onClick={(e) => {
              e.stopPropagation();
              onAdjustDates();
            }}
            data-testid={`adjust-dates-btn-${batch.id}`}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Adjust Dates
          </Button>
        )}

        {batch.notes && (
          <p className="text-xs text-gray-600 pt-2 border-t border-gray-100 truncate">{batch.notes}</p>
        )}
      </CardContent>
    </Card>
  );
};

// Compost Date Adjustment Modal
const CompostDateAdjustModal = ({ batch, onClose, onSuccess }) => {
  const [selectedField, setSelectedField] = useState('start_date');
  const [newDate, setNewDate] = useState(batch.start_date ? batch.start_date.split('T')[0] : '');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const dateFields = [
    { field: 'start_date', label: 'Start Date (Prewetting)', current: batch.start_date },
    { field: 'spawning_date', label: 'Spawning Date (Day 20)', current: batch.spawning_date }
  ];

  const handleSubmit = async () => {
    if (!newDate) {
      toast.error('Please select a new date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/compost/batches/${batch.id}/update-date`,
        {
          date_field: selectedField,
          new_date: new Date(newDate).toISOString(),
          reason: reason || 'Date adjusted'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Batch date updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update date');
    } finally {
      setLoading(false);
    }
  };

  const selectedFieldData = dateFields.find(f => f.field === selectedField);
  const originalDate = selectedFieldData?.current ? new Date(selectedFieldData.current) : null;
  const isDelay = originalDate && newDate && new Date(newDate) > originalDate;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]" data-testid="compost-date-adjustment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1A4D2E]" />
            Adjust Dates - {batch.batch_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Current Day: <span className="font-medium">{batch.current_day || 1}/20</span>
          </p>

          {/* Date Field Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date to Adjust
            </label>
            <div className="space-y-2">
              {dateFields.map((df) => (
                <button
                  key={df.field}
                  onClick={() => {
                    setSelectedField(df.field);
                    setNewDate(df.current ? df.current.split('T')[0] : '');
                  }}
                  className={`w-full p-3 text-left rounded-lg border transition-all ${
                    selectedField === df.field
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`date-field-${df.field}`}
                >
                  <p className="text-sm font-medium">{df.label}</p>
                  <p className="text-xs text-gray-500">
                    Current: {df.current ? format(new Date(df.current), 'MMM d, yyyy') : 'Not set'}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* New Date Input */}
          <div>
            <Label htmlFor="newDate">New Date</Label>
            <Input
              id="newDate"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="mt-1"
              data-testid="new-date-input"
            />
            {isDelay && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                This is a delay - Owner will be notified
              </p>
            )}
            {selectedField === 'start_date' && newDate && (
              <p className="text-xs text-blue-600 mt-1">
                Spawning date will be recalculated to {format(addDays(new Date(newDate), 19), 'MMM d, yyyy')}
              </p>
            )}
          </div>

          {/* Reason Input */}
          <div>
            <Label htmlFor="reason">Reason for Change</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this date being changed?"
              className="mt-1"
              rows={2}
              data-testid="reason-input"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !newDate}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              data-testid="submit-date-change"
            >
              {loading ? 'Updating...' : 'Update Date'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateBatchModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    batch_name: '',
    start_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.batch_name) {
      toast.error('Please enter a batch name');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/compost/batches`, {
        ...formData,
        start_date: new Date(formData.start_date).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="create-batch-modal">
        <DialogHeader>
          <DialogTitle>Create Composting Batch</DialogTitle>
          <p className="text-sm text-gray-500 mt-2">Start a new 20-day composting cycle.</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="batch_name">Batch Name</Label>
            <Input
              id="batch_name"
              data-testid="batch-name-input"
              type="text"
              placeholder="e.g., Batch 2025-02-A"
              value={formData.batch_name}
              onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="start_date">Start Date</Label>
            <Input
              id="start_date"
              data-testid="batch-start-date-input"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Expected spawning: {format(addDays(new Date(formData.start_date), 19), 'PP')}</p>
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              data-testid="batch-notes-input"
              placeholder="Any special notes for this batch..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-batch-button"
              disabled={submitting}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              {submitting ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BatchDetailModal = ({ batch, schedule, onClose, onUpdate }) => {
  const [batchDetail, setBatchDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/compost/batches/${batch.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBatchDetail(response.data);
      } catch (error) {
        toast.error('Failed to load batch details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [batch.id]);

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px]">
          <div className="text-center py-8 text-gray-500">Loading batch details...</div>
        </DialogContent>
      </Dialog>
    );
  }

  const completedTasks = batchDetail?.task_logs || [];
  const completedDays = new Set(completedTasks.map(t => t.day));

  // Filter schedule to show only actionable days (skip rest and pasteurization monitoring)
  const actionableSchedule = schedule.filter(day => {
    if (day.task.toLowerCase().includes('rest')) return false;
    if (day.task.toLowerCase().includes('pasteurization monitoring') && day.day >= 13 && day.day <= 18) return false;
    return true;
  });

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto" data-testid="batch-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{batch.batch_name}</DialogTitle>
          <div className="flex gap-2 mt-2">
            <Badge className={batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {batch.status}
            </Badge>
            <span className="text-sm text-gray-500">Started: {format(new Date(batch.start_date), 'PP')}</span>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {batch.notes && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">{batch.notes}</p>
            </div>
          )}

          <div>
            <h3 className="font-medium mb-3">Task Progress (Key Actions Only)</h3>
            <div className="space-y-2">
              {actionableSchedule.map((day) => {
                const isCompleted = completedDays.has(day.day);
                const taskLog = completedTasks.find(t => t.day === day.day);
                const start = new Date(batch.start_date);
                const taskDate = addDays(start, day.day - 1);
                const isToday = format(new Date(), 'yyyy-MM-dd') === format(taskDate, 'yyyy-MM-dd');

                return (
                  <div
                    key={day.day}
                    className={`p-3 rounded-lg border ${
                      isCompleted ? 'bg-green-50 border-green-200' :
                      isToday ? 'bg-blue-50 border-blue-200' :
                      'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        ) : isToday ? (
                          <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                        ) : (
                          <div className="w-5 h-5 mt-0.5 rounded-full border-2 border-gray-300" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">Day {day.day}: {day.task}</p>
                            {isToday && <Badge variant="outline" className="text-xs">Today</Badge>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{format(taskDate, 'PP')}</p>
                          {taskLog && taskLog.notes && (
                            <p className="text-xs text-gray-600 mt-2 bg-white p-2 rounded">{taskLog.notes}</p>
                          )}
                          {taskLog && taskLog.photo_id && (
                            <div className="mt-2">
                              <TaskPhotoThumb photoId={taskLog.photo_id} size="w-16 h-16" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BatchToRoomAssignmentModal = ({ batches, rooms, onClose, onSuccess }) => {
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBatch || !selectedRoom) {
      toast.error('Please select both batch and room');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const batch = batches.find(b => b.id === selectedBatch);
      
      await axios.post(`${API}/room-cycles`, {
        room_id: selectedRoom,
        batch_id: batch.id,
        batch_name: batch.batch_name,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign batch');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="batch-to-room-modal">
        <DialogHeader>
          <DialogTitle>Assign Batch to Room</DialogTitle>
          <p className="text-sm text-gray-500 mt-2">
            Select a completed compost batch and assign it to an available room for the 45-day room cycle.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="batch">Select Batch (Day 20+ Completed)</Label>
            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger className="mt-1" data-testid="batch-select">
                <SelectValue placeholder="Choose a batch" />
              </SelectTrigger>
              <SelectContent>
                {batches.length === 0 ? (
                  <SelectItem value="none" disabled>No batches ready</SelectItem>
                ) : (
                  batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.batch_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="room">Select Available Room</Label>
            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger className="mt-1" data-testid="room-select">
                <SelectValue placeholder="Choose a room" />
              </SelectTrigger>
              <SelectContent>
                {rooms.length === 0 ? (
                  <SelectItem value="none" disabled>No rooms available</SelectItem>
                ) : (
                  rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.name} - {room.status}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              data-testid="assignment-notes-input"
              placeholder="Any special notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="confirm-assignment-button"
              disabled={submitting || batches.length === 0 || rooms.length === 0}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              {submitting ? 'Assigning...' : 'Assign to Room'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Full Composting Calendar with PDF/Excel Export
const CompostingCalendarFull = ({ batches }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Composting schedule milestones with colors
  const COMPOSTING_MILESTONES = [
    { day: 1, task: 'Prewetting Start', color: '#16A34A' },
    { day: 4, task: 'C.M/Gypsum/Urea Mixing', color: '#F59E0B' },
    { day: 5, task: 'Bunker Filling', color: '#D97706' },
    { day: 7, task: '1st Turning', color: '#3B82F6' },
    { day: 10, task: '2nd Turning', color: '#6366F1' },
    { day: 12, task: '3rd Turning', color: '#8B5CF6' },
    { day: 14, task: 'Tunnel Filling', color: '#EC4899' },
    { day: 15, task: 'Pasturization Start', color: '#EF4444' },
    { day: 20, task: 'Spawning Ready', color: '#A855F7' },
  ];

  useEffect(() => {
    generateCalendarEvents();
  }, [batches]);

  const generateCalendarEvents = () => {
    const calendarEvents = [];
    
    batches.forEach(batch => {
      const startDate = new Date(batch.start_date);
      
      // Add all composting milestones for this batch
      COMPOSTING_MILESTONES.forEach(milestone => {
        const eventDate = addDays(startDate, milestone.day - 1);
        calendarEvents.push({
          id: `${batch.id}-${milestone.task}`,
          date: eventDate,
          title: `${batch.batch_name} - ${milestone.task}`,
          batchName: batch.batch_name,
          task: milestone.task,
          color: milestone.color,
          type: 'composting'
        });
      });
    });
    
    setEvents(calendarEvents);
  };

  const handleExport = async (exportFormat) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/composting-calendar?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `composting_schedule.${exportFormat === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Calendar exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export calendar');
    } finally {
      setExporting(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => isSameDay(new Date(event.date), day));
  };

  const days = getDaysInMonth();
  const firstDayOfMonth = startOfMonth(currentMonth).getDay();

  return (
    <div className="space-y-6">
      {/* Calendar Header with Export */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-medium px-4 text-lg">{format(currentMonth, 'MMMM yyyy')}</span>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('excel')}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[100px] bg-gray-50 rounded" />
          ))}
          
          {/* Days of the month */}
          {days.map(day => {
            const dayEvents = getEventsForDay(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <div
                key={day.toISOString()}
                className={`min-h-[100px] p-1 border rounded ${
                  isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                } ${!isSameMonth(day, currentMonth) ? 'opacity-50' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1 overflow-y-auto max-h-[80px]">
                  {dayEvents.slice(0, 3).map((event, idx) => (
                    <div
                      key={idx}
                      className="text-xs p-1 rounded truncate"
                      style={{ backgroundColor: `${event.color}20`, borderLeft: `3px solid ${event.color}` }}
                      title={event.title}
                    >
                      {event.task}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="pt-4 border-t">
        <p className="text-sm font-medium text-gray-700 mb-3">Legend</p>
        <div className="flex flex-wrap gap-3">
          {COMPOSTING_MILESTONES.map((milestone, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: milestone.color }} />
              <span className="text-xs text-gray-600">{milestone.task}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompostManagement;
